import { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";

import { SenderRecipientOption } from "../components/SenderRecipientDropdown";

import { conversationSenderUserId } from "./lastSender.util";

export type ChannelType = "email" | "linkedin" | undefined;

export interface ResolvedChannel {
	channelType: ChannelType;
	senderOption: SenderRecipientOption | undefined;
	recipientOption: SenderRecipientOption | undefined;
	/** Who sent (or received) the conversation the channel was taken from, when it names one. */
	basisUser?: Conversation["user"];
	/** That user's id, also when the conversation carries only `userId` (no user object). */
	basisUserId?: number;
}

const SENT_TYPES = ["mail_sent", "mail_scheduled", "message_sent", "message_scheduled"];
const EMAIL_TYPES = ["mail_sent", "mail_received", "mail_scheduled"];
const LINKEDIN_TYPES = ["message_sent", "message_received", "message_scheduled"];

const isSentType = (type: string): boolean => SENT_TYPES.includes(type);

const byMostRecent = (a: Conversation, b: Conversation): number =>
	new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime();

/**
 * A calendar mail is stored as an ordinary `mail_received` conversation: an inbound invitation is
 * flagged `hasCalendarInvite`, the lead's accept/decline of ours carries `calendarResponse`
 * (PREC-3901). These are automated system messages, not genuine outreach, so they must never drive
 * the default channel/sender (PREC-3627): an inbound calendar accept would otherwise flip a LinkedIn
 * thread to email and pick whichever teammate's address happened to receive it.
 */
const isGenuine = (c: Conversation): boolean => !c.hasCalendarInvite && !c.calendarResponse;

const channelOf = (type: string | undefined): ChannelType => {
	if (type && EMAIL_TYPES.includes(type)) {
		return "email";
	}

	if (type && LINKEDIN_TYPES.includes(type)) {
		return "linkedin";
	}

	return undefined;
};

/**
 * The sender option that genuinely matches a conversation — for email the exact sending/receiving
 * address, then the conversation's sending user; for LinkedIn the sending user. Never substitutes
 * an arbitrary teammate who did not participate (PREC-3627 — that is how a non-participant like
 * "Jon" was pre-selected). Returns `undefined` when no genuine match exists, leaving the composer to
 * prompt for an explicit sender rather than guessing.
 */
const matchSenderOption = (
	basis: Conversation,
	channelType: "email" | "linkedin",
	senderOptions: SenderRecipientOption[]
): SenderRecipientOption | undefined => {
	const conversationUserId = conversationSenderUserId(basis);

	if (channelType === "linkedin") {
		return senderOptions.find(
			(opt) => opt.type === "linkedin" && opt.userId === conversationUserId
		);
	}

	const isSent = basis.type === "mail_sent" || basis.type === "mail_scheduled";
	const ourEmail = isSent ? basis.senderEmail : basis.receiverEmail;
	// A disabled mailbox needs a reconnect and cannot send (PREC-4312 E3): the thread's sender then
	// counts as gone, and the composer falls back to the user's own default.
	const mailboxes = senderOptions.filter((opt) => opt.type === "email" && !opt.disabled);

	return (
		mailboxes.find((opt) => opt.emailAddress === ourEmail) ??
		mailboxes.find((opt) => opt.userId === conversationUserId)
	);
};

/**
 * Choose the conversation that dictates a reply's channel + sender.
 *
 * The **last sent** message wins — its channel and sender are "the person already in contact with
 * the lead". This membership's own history is preferred; only when it has none do we look at the
 * prospect's last sent message across every membership (the Lead Panel opens the prospect's
 * most-recent membership, which may be empty while the real outreach sits under an older one —
 * PREC-3627). A genuine inbound message is used only as a final fallback (reply from the address it
 * reached); calendar confirmations are already filtered out upstream.
 */
const pickBasisConversation = (
	scoped: Conversation[],
	genuineByRecency: Conversation[]
): Conversation | undefined =>
	scoped.find((c) => isSentType(c.type)) ??
	genuineByRecency.find((c) => isSentType(c.type)) ??
	scoped[0];

/**
 * Given a lead's conversations, determine the channel (email vs LinkedIn) and the sender/recipient
 * to pre-fill a fresh reply draft. See {@link pickBasisConversation} and {@link matchSenderOption}
 * for the "continue as the last sender, never a calendar confirmation, never an arbitrary teammate"
 * rules (PREC-3627).
 */
export function resolveInboxChannel(
	conversations: Conversation[],
	listPersonId: number | undefined,
	senderOptions: SenderRecipientOption[],
	recipientOptions: SenderRecipientOption[]
): ResolvedChannel {
	const genuineByRecency = conversations.filter(isGenuine).sort(byMostRecent);
	const scoped = genuineByRecency.filter((c) => c.listPersonId === listPersonId);

	const basis = pickBasisConversation(scoped, genuineByRecency);
	const channelType = channelOf(basis?.type);

	if (!basis || !channelType) {
		return { channelType: undefined, senderOption: undefined, recipientOption: undefined };
	}

	return {
		channelType,
		senderOption: matchSenderOption(basis, channelType, senderOptions),
		recipientOption: recipientOptions.find((opt) => opt.type === channelType),
		basisUser: basis.user,
		basisUserId: conversationSenderUserId(basis),
	};
}
